import { z } from 'zod';
import { idListParam, nullableText, optionalText, userIdList } from '../common.js';
import { addDays, diffDays, isRealDate } from './dates.js';
/**
 * Verträge der Termine im Modul „Termine und Aufgaben“ (KON-033, KON-052). Zeiten sind Uhrzeiten in der Zeitzone des Betriebs (Europe/Berlin),
 * als Text der Form JJJJ-MM-TTTHH:MM. Ganztägige Termine haben nur Daten (JJJJ-MM-TT), das Ende ist dort der letzte Tag. So hängt nichts an der Zeitzone
 * des Browsers, und die Sommerzeitumstellung rechnet die Datenbank.
 */
const title = z.string().trim().min(2, 'Titel fehlt').max(200);
const moment = z.string().trim().max(16);
export const createAppointmentInput = z.object({
    title,
    description: optionalText(5000),
    location: optionalText(200),
    allDay: z.boolean().default(false),
    start: moment,
    end: moment,
    /** Art des Termins (Terminarten, Einrichtung), freiwillig */
    typeId: z.uuid('Ungültige Art').optional(),
    /** Teilnehmer: aktive Benutzer desselben Mandanten */
    participantUserIds: userIdList.optional(),
});
export const updateAppointmentInput = z
    .object({
    title: title.optional(),
    description: nullableText(5000),
    location: nullableText(200),
    allDay: z.boolean().optional(),
    start: moment.optional(),
    end: moment.optional(),
    /** Art zuordnen, null entfernt sie */
    typeId: z.uuid('Ungültige Art').nullable().optional(),
    participantUserIds: userIdList.optional(),
})
    .strict()
    .refine((v) => Object.values(v).some((x) => x !== undefined), 'Keine Änderung angegeben');
/** Termine in einem Zeitraum von höchstens 100 Tagen, das reicht für ein Monatsraster mit sechs Wochen */
export const MAX_RANGE_DAYS = 100;
export const listAppointmentsQuery = z
    .object({
    from: z.iso.date('Ungültiges Datum'),
    to: z.iso.date('Ungültiges Datum'),
    /** Termine, an denen der Benutzer teilnimmt („Meine Termine“) */
    involvedUserId: z.uuid().optional(),
    /** Termine mehrerer Personen zugleich, durch Kommas getrennt: Es zählen Termine, an denen mindestens eine teilnimmt */
    involvedUserIds: idListParam,
    /** Nur Termine dieser Art */
    typeId: z.uuid().optional(),
    /** Mehrere Arten zugleich, durch Kommas getrennt */
    typeIds: idListParam,
    q: z.string().trim().max(100).optional(),
})
    .superRefine((v, ctx) => {
    if (v.to < v.from)
        ctx.addIssue({ code: 'custom', path: ['to'], message: 'Das Ende darf nicht vor dem Beginn liegen.' });
    else if (diffDays(v.from, v.to) >= MAX_RANGE_DAYS)
        ctx.addIssue({ code: 'custom', path: ['to'], message: `Höchstens ${MAX_RANGE_DAYS} Tage auf einmal.` });
});
const DATE = /^\d{4}-\d{2}-\d{2}$/;
const DATETIME = /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})$/;
/** Ist das ein echtes Datum oder eine echte Uhrzeit in der richtigen Form? Gibt die Meldung zurück oder null, wenn alles stimmt. */
function checkMoment(value, allDay) {
    if (allDay) {
        if (!DATE.test(value))
            return 'Bitte ein Datum angeben.';
        return isRealDate(value) ? null : 'Ungültiges Datum.';
    }
    const m = DATETIME.exec(value);
    if (!m)
        return 'Bitte Datum und Uhrzeit angeben.';
    if (!isRealDate(m[1]))
        return 'Ungültiges Datum.';
    if (Number(m[2]) > 23 || Number(m[3]) > 59)
        return 'Ungültige Uhrzeit.';
    return null;
}
/**
 * Prüft Beginn und Ende eines Termins und nennt die Meldung je Feld. Ein ganztägiger Termin hat nur Daten und darf am selben Tag enden, ein Termin
 * mit Uhrzeit muss nach dem Beginn enden. Die Dauer ist begrenzt (31 Tage mit Uhrzeit, 366 Tage ganztägig), damit sich kein Termin versehentlich über Jahre zieht.
 */
export function validateSchedule(input) {
    const errors = {};
    const startError = checkMoment(input.start, input.allDay);
    const endError = checkMoment(input.end, input.allDay);
    if (startError)
        errors.start = startError;
    if (endError)
        errors.end = endError;
    if (startError || endError)
        return errors;
    if (input.allDay) {
        if (input.end < input.start)
            errors.end = 'Das Enddatum darf nicht vor dem Beginn liegen.';
        else if (diffDays(input.start, input.end) >= 366)
            errors.end = 'Ein Termin kann höchstens ein Jahr dauern.';
    }
    else {
        if (input.end <= input.start)
            errors.end = 'Das Ende muss nach dem Beginn liegen.';
        else if (diffDays(input.start.slice(0, 10), input.end.slice(0, 10)) > 31)
            errors.end = 'Ein Termin mit Uhrzeit kann höchstens 31 Tage dauern.';
    }
    return errors;
}
/** Zeitpunkte, die die Datenbank speichert, als Text der Form JJJJ-MM-TTTHH:MM in Europe/Berlin. Ganztägig: Beginn des ersten und Beginn des Tages nach dem letzten Tag. */
export function scheduleToWall(input) {
    return input.allDay ? { startsAt: `${input.start}T00:00`, endsAt: `${addDays(input.end, 1)}T00:00` } : { startsAt: input.start, endsAt: input.end };
}
//# sourceMappingURL=appointments.js.map